/*
 * Figma `121:914` — OPS-05 · Procurement · RFQ + Budget.
 *
 * `TableTemplate`: the RFQ register with the one you opened in a drawer, and a budget panel beside
 * the totals.
 *
 * The brief asks procurement to show "RFQ comparison, approval, budget guardrail, and timeline
 * consequence", and the piece the old panel left to the reader is the first. Three bids on a row
 * are three numbers; what decides anything is the spread between them, which one is cheapest, and
 * whether the one being awarded fits the budget. `comparisonOf` computes that once, so the table
 * and the drawer cannot disagree about which bid is lowest.
 *
 * Awarding is the consequential action and the server has two rules about it: an RFQ cannot be
 * awarded without a winning bid, and the winning bid will not delete while it is the winner. Both
 * are mirrored here so nobody reaches a refusal they could have been told about first — and both
 * still hold server-side if they do.
 */
import { useState } from 'react';
import {
  Alert, Button, Card, ConfirmDialog, DateTimeField, Drawer, KpiCard, MeterRow, StatusBadge,
  TableTemplate, TextField,
} from '../..';
import type { BadgeTone, Column } from '../..';
import type { Destination } from '../../destinations';
import type { OpsBidDto, OpsRfqDto, OpsRfqStatus } from '../../../api';
import type { OpsProcurementState } from './useOpsProcurement';
import { useOpsProcurement } from './useOpsProcurement';
import type { OpsMarketingState } from './useOpsMarketing';
import { useOpsMarketing } from './useOpsMarketing';
import { MarketingPanel } from './MarketingPanel';

const baht = (n: number) => `฿${Math.round(n).toLocaleString('th-TH')}`;
const day = (iso: string) => (iso ? new Date(iso).toLocaleDateString('th-TH') : '—');

const STATUS_LABEL: Record<OpsRfqStatus, string> = {
  open: 'เปิดรับราคา', awarded: 'ตัดสินแล้ว', cancelled: 'ยกเลิก',
};
const STATUS_TONE: Record<OpsRfqStatus, BadgeTone> = {
  open: 'warning', awarded: 'success', cancelled: 'neutral',
};

export function OpsProcurementScreen({ destination, state, marketing, permitted, role }: {
  destination: Destination;
  state: OpsProcurementState;
  /*
   * The other half of this destination. Optional so the RFQ half can still be rendered on its own
   * in a test, and because the two have separate fetches — one failing should not blank the other.
   */
  marketing?: OpsMarketingState;
  permitted: boolean;
  role?: string | null;
}) {
  const {
    rfqs, comparisons, loading, loadError, error, busyId,
    create, update, award, addBid, removeBid, remove, reload,
  } = state;
  const [openId, setOpenId] = useState<string | null>(null);
  const [confirmAward, setConfirmAward] = useState<{ rfq: OpsRfqDto; bid: OpsBidDto } | null>(null);
  const [vendor, setVendor] = useState('');
  const [amount, setAmount] = useState('');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ title: '', category: '', deadline: '', budget: '' });

  const open = openId ? rfqs.find((r) => r.id === openId) ?? null : null;
  const comparison = open ? comparisons.get(open.id) ?? null : null;

  const openRfqs = rfqs.filter((r) => r.status === 'open');
  const needingBids = openRfqs.filter((r) => r.bids.length === 0);
  const decidable = openRfqs.filter((r) => r.bids.length > 0);
  const awardedOverBudget = rfqs.filter((r) => {
    const c = comparisons.get(r.id);
    return r.status === 'awarded' && c?.overBudget !== null && (c?.overBudget ?? 0) > 0;
  });
  const totalAwarded = rfqs.reduce((sum, r) => sum + (comparisons.get(r.id)?.awarded?.totalBidAmount ?? 0), 0);
  const totalBudget = rfqs.reduce((sum, r) => sum + (r.budgetTarget ?? 0), 0);

  const columns: Column<OpsRfqDto>[] = [
    {
      key: 'title',
      header: 'รายการ',
      cell: (r) => (
        <span className="jt-cell-stack">
          <span>{r.title}</span>
          <span className="jt-cell-sub">{r.category || 'ไม่ระบุหมวด'} · ปิดรับ {day(r.deadline)}</span>
        </span>
      ),
    },
    {
      key: 'bids',
      header: 'ใบเสนอราคา',
      width: '210px',
      cell: (r) => {
        const c = comparisons.get(r.id);
        if (!c || r.bids.length === 0) return <StatusBadge tone="warning">ยังไม่มีใครเสนอ</StatusBadge>;
        return (
          <span className="jt-cell-stack">
            <span className="jt-mono">{r.bids.length} ราย · ถูกสุด {baht(c.cheapest!.totalBidAmount)}</span>
            {c.spread > 0 && <span className="jt-cell-sub">ต่างกัน {baht(c.spread)}</span>}
          </span>
        );
      },
    },
    {
      key: 'budget',
      header: 'เทียบงบ',
      width: '180px',
      cell: (r) => {
        const c = comparisons.get(r.id);
        if (!c || c.overBudget === null) return <span className="jt-cell-sub">ไม่ได้ตั้งงบ</span>;
        return c.overBudget > 0
          ? <StatusBadge tone="danger">{`เกินงบ ${baht(c.overBudget)}`}</StatusBadge>
          : <StatusBadge tone="success">{`ต่ำกว่างบ ${baht(-c.overBudget)}`}</StatusBadge>;
      },
    },
    {
      key: 'status',
      header: 'สถานะ',
      width: '170px',
      cell: (r) => (
        <span className="jt-cell-stack">
          <StatusBadge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</StatusBadge>
          {comparisons.get(r.id)?.awarded && (
            <span className="jt-cell-sub">{comparisons.get(r.id)!.awarded!.vendorName}</span>
          )}
        </span>
      ),
    },
  ];

  return (
    <TableTemplate
      gate={{ destination, loading, error: loadError || null, onRetry: reload, permitted, role }}
      kpis={[
        <KpiCard key="open" label="RFQ ที่เปิดอยู่" value={openRfqs.length.toLocaleString('th-TH')} />,
        <KpiCard
          key="decide"
          label="พร้อมตัดสิน"
          value={decidable.length.toLocaleString('th-TH')}
          tone={decidable.length > 0 ? 'warning' : undefined}
          meta="มีใบเสนอราคาแล้ว รอคนตัดสิน"
        />,
        <KpiCard
          key="nobids"
          label="ยังไม่มีใครเสนอ"
          value={needingBids.length.toLocaleString('th-TH')}
          tone={needingBids.length > 0 ? 'danger' : undefined}
        />,
        <KpiCard key="spent" label="ยอดที่ตัดสินแล้ว" value={baht(totalAwarded)} meta={totalBudget > 0 ? `จากงบ ${baht(totalBudget)}` : undefined} />,
      ]}
      summary={
        <>
          {error && <Alert tone="danger" title="ทำรายการไม่สำเร็จ">{error}</Alert>}
          {awardedOverBudget.length > 0 && (
            <Alert tone="warning" title={`${awardedOverBudget.length} รายการตัดสินเกินงบที่ตั้งไว้`}>
              {awardedOverBudget.map((r) => `${r.title} เกิน ${baht(comparisons.get(r.id)!.overBudget!)}`).join(' · ')}
            </Alert>
          )}
          {totalBudget > 0 && (
            <Card title="งบประมาณ" sub="เทียบยอดที่ตัดสินแล้วกับงบที่ตั้งไว้ในทุก RFQ">
              <MeterRow
                label="ใช้ไปแล้ว"
                value={`${baht(totalAwarded)} จาก ${baht(totalBudget)}`}
                percent={(totalAwarded / totalBudget) * 100}
                tone={totalAwarded > totalBudget ? 'danger' : totalAwarded / totalBudget > 0.8 ? 'warning' : 'positive'}
                caption={totalAwarded > totalBudget ? `เกินงบรวม ${baht(totalAwarded - totalBudget)}` : undefined}
              />
            </Card>
          )}
          {/*
            * The second half of "จัดซื้อและการตลาด". Above the RFQ register rather than below it,
            * because money going out on ads is the part of this destination somebody comes back to
            * weekly, while an RFQ is read once around the decision.
            */}
          {marketing && <MarketingPanel state={marketing} />}
        </>
      }
      toolbar={
        adding ? (
          <>
            <TextField label="รายการที่จัดซื้อ" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
            <TextField label="หมวด" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} />
            <DateTimeField label="ปิดรับราคา" mode="date" value={draft.deadline} onChange={(e) => setDraft({ ...draft, deadline: e.target.value })} />
            <TextField label="งบที่ตั้งไว้" type="number" min={0} value={draft.budget} onChange={(e) => setDraft({ ...draft, budget: e.target.value })} />
            <Button
              size="sm"
              variant="primary"
              disabled={!draft.title.trim() || !draft.deadline}
              onClick={async () => {
                await create({
                  title: draft.title.trim(),
                  category: draft.category.trim(),
                  deadline: draft.deadline,
                  budgetTarget: draft.budget ? Number(draft.budget) : null,
                });
                setDraft({ title: '', category: '', deadline: '', budget: '' });
                setAdding(false);
              }}
            >
              สร้าง RFQ
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>ยกเลิก</Button>
          </>
        ) : (
          <Button size="sm" variant="primary" onClick={() => setAdding(true)}>＋ สร้าง RFQ</Button>
        )
      }
      table={{
        label: 'รายการจัดซื้อ',
        columns,
        rows: rfqs,
        rowKey: (r) => r.id,
        emptyTitle: 'ยังไม่มีรายการจัดซื้อ',
        emptyText: 'สร้าง RFQ เพื่อเริ่มเก็บใบเสนอราคาและเปรียบเทียบ',
        onRowClick: (r) => { setOpenId(r.id); setVendor(''); setAmount(''); },
        activeRowKey: openId,
        card: (r) => (
          <span className="jt-cell-stack">
            <span>{r.title}</span>
            <span className="jt-cell-sub">{r.bids.length} ใบเสนอราคา · ปิดรับ {day(r.deadline)}</span>
            <StatusBadge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</StatusBadge>
          </span>
        ),
      }}
      drawer={
        <>
          <Drawer
            open={!!open}
            onClose={() => setOpenId(null)}
            title={open?.title ?? ''}
            footer={open && (
              <Button
                variant="ghost"
                disabled={busyId === open.id || open.status === 'awarded'}
                onClick={() => void remove(open)}
              >
                ลบ RFQ
              </Button>
            )}
          >
            {open && comparison && (
              <>
                {open.status === 'awarded' && (
                  <Alert tone="success" title={`ตัดสินให้ ${comparison.awarded?.vendorName ?? '—'} แล้ว`}>
                    RFQ ที่ตัดสินแล้วลบไม่ได้ และใบเสนอราคาของผู้ชนะลบไม่ได้จนกว่าจะเปลี่ยนผู้ชนะ
                  </Alert>
                )}
                {comparison.overBudget !== null && comparison.overBudget > 0 && (
                  <Alert tone="warning" title={`เกินงบที่ตั้งไว้ ${baht(comparison.overBudget)}`}>
                    งบที่ตั้งไว้คือ {baht(open.budgetTarget ?? 0)} — ตัดสินได้ แต่จะกินงบส่วนอื่น
                  </Alert>
                )}
                {open.bids.length === 0 && (
                  <Alert tone="warning" title="ยังไม่มีใครเสนอราคา">
                    ตัดสินไม่ได้จนกว่าจะมีใบเสนอราคาอย่างน้อยหนึ่งใบ
                  </Alert>
                )}

                <Card title={`ใบเสนอราคา · ${open.bids.length}`} sub={comparison.spread > 0 ? `ถูกสุดกับแพงสุดต่างกัน ${baht(comparison.spread)}` : undefined}>
                  <ul className="jt-account-list">
                    {[...open.bids].sort((a, b) => a.totalBidAmount - b.totalBidAmount).map((bid) => {
                      const isWinner = bid.id === open.awardedBidId;
                      const isCheapest = bid.id === comparison.cheapest?.id;
                      return (
                        <li key={bid.id} className="jt-band-row">
                          <div className="jt-band-copy">
                            <span className="jt-band-name">{bid.vendorName} · {baht(bid.totalBidAmount)}</span>
                            {bid.notes && <span className="jt-band-usage">{bid.notes}</span>}
                          </div>
                          <div className="jt-inline-actions">
                            {isCheapest && <StatusBadge tone="accent">ถูกที่สุด</StatusBadge>}
                            {isWinner && <StatusBadge tone="success">ผู้ชนะ</StatusBadge>}
                            {!isWinner && open.status !== 'awarded' && (
                              <Button size="sm" variant="primary" disabled={busyId === open.id} onClick={() => setConfirmAward({ rfq: open, bid })}>
                                ตัดสินให้รายนี้
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={busyId === open.id || isWinner}
                              title={isWinner ? 'ใบเสนอราคาของผู้ชนะลบไม่ได้' : undefined}
                              onClick={() => void removeBid(open, bid)}
                            >
                              ลบ
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </Card>

                <Card title="เพิ่มใบเสนอราคา">
                  <div className="jt-field-grid">
                    <TextField label="ชื่อผู้ขาย" value={vendor} onChange={(e) => setVendor(e.target.value)} />
                    <TextField label="ราคาเสนอ" type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} />
                  </div>
                  <div className="jt-inline-actions">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busyId === open.id || !vendor.trim() || !Number(amount)}
                      onClick={async () => {
                        await addBid(open, vendor.trim(), Number(amount), '');
                        setVendor('');
                        setAmount('');
                      }}
                    >
                      เพิ่ม
                    </Button>
                  </div>
                </Card>

                <Card title="งบและกำหนดเวลา">
                  <TextField
                    label="งบที่ตั้งไว้"
                    type="number"
                    min={0}
                    value={open.budgetTarget ?? ''}
                    onChange={(e) => void update(open, { budgetTarget: e.target.value ? Number(e.target.value) : null })}
                  />
                  <DateTimeField
                    label="ปิดรับราคา"
                    mode="date"
                    value={open.deadline?.slice(0, 10) ?? ''}
                    onChange={(e) => void update(open, { deadline: e.target.value })}
                  />
                </Card>
              </>
            )}
          </Drawer>

          <ConfirmDialog
            open={!!confirmAward}
            onClose={() => setConfirmAward(null)}
            onConfirm={() => {
              const target = confirmAward;
              setConfirmAward(null);
              if (target) void award(target.rfq, target.bid.id);
            }}
            title={`ตัดสินให้ ${confirmAward?.bid.vendorName ?? ''}`}
            confirmLabel="ตัดสินให้รายนี้"
            tone="warning"
            consequences={[
              `ยอดที่ตัดสินคือ ${confirmAward ? baht(confirmAward.bid.totalBidAmount) : ''}`,
              ...(confirmAward && comparisons.get(confirmAward.rfq.id)?.cheapest
                && confirmAward.bid.id !== comparisons.get(confirmAward.rfq.id)!.cheapest!.id
                ? [`ไม่ใช่รายที่ถูกที่สุด — ถูกสุดคือ ${comparisons.get(confirmAward.rfq.id)!.cheapest!.vendorName} ที่ ${baht(comparisons.get(confirmAward.rfq.id)!.cheapest!.totalBidAmount)}`]
                : []),
              'RFQ ที่ตัดสินแล้วจะลบไม่ได้ และใบเสนอราคาของผู้ชนะจะลบไม่ได้จนกว่าจะเปลี่ยนผู้ชนะ',
            ]}
          />
        </>
      }
    />
  );
}

export function OpsProcurementDestination({ token, eventSlug, destination, permitted, role }: {
  token: string;
  eventSlug?: string;
  destination: Destination;
  permitted: boolean;
  role?: string | null;
}) {
  const state = useOpsProcurement(token, eventSlug);
  const marketing = useOpsMarketing(token, eventSlug);
  return (
    <OpsProcurementScreen
      destination={destination} state={state} marketing={marketing} permitted={permitted} role={role}
    />
  );
}
